import React from 'react';
import {
    Box,
    Stack,
    Typography,
    Paper,
    Button,
} from '@mui/material';
import { History } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useRecentlyViewed } from '../../../hooks/common/useRecentlyViewed';
import { formatMoney } from '../../../utils/money';

/**
 * Horizontal carousel of the customer's recently viewed products,
 * sourced from useRecentlyViewed (localStorage). Renders nothing
 * when the list is empty so home / PDP don't show a blank section.
 */
const RecentlyViewed = ({ excludeProductId }) => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();
    const { items, clear } = useRecentlyViewed();

    const list = items.filter((p) => p.id !== excludeProductId);
    if (list.length === 0) return null;

    return (
        <Box sx={{ my: 5 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <History fontSize="small" />
                    <Typography variant="h6" fontWeight={800}>
                        {t('recentlyViewed.title', 'Recently viewed')}
                    </Typography>
                </Stack>
                <Button size="small" onClick={clear} color="inherit">
                    {t('recentlyViewed.clear', 'Clear')}
                </Button>
            </Stack>

            <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1.5 }}>
                {list.map((p) => {
                    const name = p.name?.[isArabic ? 'ar' : 'en']
                        || p.name?.en
                        || p.name
                        || '';
                    return (
                        <Paper
                            key={p.id}
                            elevation={0}
                            variant="outlined"
                            onClick={() => navigate(`/products/${p.slug}`)}
                            sx={{
                                minWidth: 160,
                                width: 160,
                                p: 1.25,
                                borderRadius: 2,
                                cursor: 'pointer',
                                flexShrink: 0,
                                transition: 'border-color 120ms',
                                '&:hover': { borderColor: 'primary.main' },
                            }}
                        >
                            {p.thumb_url ? (
                                <Box
                                    component="img"
                                    src={p.thumb_url}
                                    alt=""
                                    sx={{
                                        width: '100%',
                                        height: 110,
                                        borderRadius: 1.5,
                                        objectFit: 'cover',
                                        mb: 1,
                                    }}
                                />
                            ) : (
                                <Box sx={{ width: '100%', height: 110, borderRadius: 1.5, bgcolor: 'action.hover', mb: 1 }} />
                            )}
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {name}
                            </Typography>
                            {p.price != null && (
                                <Typography variant="caption" color="primary.main" fontWeight={700}>
                                    {formatMoney(p.price, p.currency)}
                                </Typography>
                            )}
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default RecentlyViewed;
